import { FIFTEEN_MINUTES } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type ColumnDef, type Table } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { PaginationOrder, QUERY_KEYS } from 'constants/common';
import { AppointmentService } from 'services/appointments';
import { useUserStore } from 'stores/user';
import { type RecentPatientDTO } from 'types/appointments';

export const useFetchPsychologistRecentsPatients = (
  columns: ColumnDef<RecentPatientDTO>[],
  pageSize: number = 10
): {
  globalFilter: string;
  query: UseQueryResult<RecentPatientDTO[], Error>;
  table: Table<RecentPatientDTO>;
  totalCount: number;
  setGlobalFilter: (value: string) => void;
} => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const psychologistId = useUserStore(state => state.user?.id) ?? '';
  const debouncedFilter = useDebounce(globalFilter, 500);

  const query = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_PSYCHOLOGIST_RECENTS_PATIENTS,
      psychologistId,
      pagination.pageIndex,
      pagination.pageSize,
      debouncedFilter,
    ],
    queryFn: () =>
      AppointmentService.getAllRecentPatients({
        psychologistId,
        offset: pagination.pageIndex * pagination.pageSize,
        limit: pagination.pageSize,
        order: PaginationOrder.RECENT_LAST,
        nickname: debouncedFilter || undefined,
      }),
    select: data => data.data.filter(patient => !patient.banned),
    placeholderData: previousData => previousData,
    enabled: !!psychologistId,
    staleTime: FIFTEEN_MINUTES,
  });

  const { data: totalCount = 0 } = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_PSYCHOLOGIST_RECENTS_PATIENTS_COUNT,
      psychologistId,
      debouncedFilter,
    ],
    queryFn: () =>
      AppointmentService.getCountAllRecentPatients({
        psychologistId,
        offset: 0,
        limit: 0,
        nickname: debouncedFilter || undefined,
      }),
    select: response => response.data.count,
    enabled: !!psychologistId,
    staleTime: FIFTEEN_MINUTES,
  });

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [debouncedFilter]);

  const table = useServerPaginatedTable<RecentPatientDTO>({
    columns,
    data: query.data ?? [],
    totalCount,
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: setGlobalFilter,
  });

  return {
    query,
    globalFilter,
    table,
    totalCount,
    setGlobalFilter,
  };
};
